import { Box, Breadcrumbs, Card, Typography } from '@mui/material';
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import AppTooltip from '@tenant/core/AppTooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IntlMessages from '@tenant/utility/IntlMessages';
import { ROUTE_STRING } from 'constants/RouteString';
import { Formik } from 'formik';
import validationSchema from '../../redux/slices/dockerImage/constants/validationSchema';
import DockerAppTypes from 'constants/DockerAppTypes';
import useAddDockerImage from 'redux/slices/dockerImage/hooks/useAddDockerImage';
import { Code } from 'react-content-loader';
import AddDockerImageForm from './components/Common/AddDockerImageForm';
import { useQueryClient } from '@tanstack/react-query';
import queryKeyDockerImage from 'redux/slices/dockerImage/constants/queryKeyDockerImage';
import * as Yup from 'yup';
import useGetLastRunRepository from 'redux/slices/dockerImage/hooks/useGetLastRunRepository';
import Error404 from 'pages/errorPages/Error404';
import useGoBack from '@tenant/utility/hooks/useGoBack';

const IS_UPGRADE = true;

const UpgradeVersion = () => {
  const goBack = useGoBack();
  const { repository } = useParams();
  const { data, isLoading, isRefetching, isError } = useGetLastRunRepository({
    repository,
  });
  const addItem = useAddDockerImage(IS_UPGRADE);
  const queryClient = useQueryClient();

  const loading = isLoading || isRefetching;

  const initialValues = data?.data?.LastRun ?? {};

  const onClickBackButton = () => {
    goBack(ROUTE_STRING.DOCKER_IMAGE.VIEW.replace(':repository', repository));
  };

  const handleSubmit = (data) => {
    addItem.mutate(
      {
        ...data,
        AppModel: undefined,
        AppId: data.AppModel?.Id,
        DeploymentType: data.DeploymentType?.Value,
        TargetPort: +data.TargetPort,
      },
      {
        onSuccess() {
          queryClient.removeQueries(queryKeyDockerImage.getDetail(repository));
          onClickBackButton();
        },
      },
    );
  };

  if (isError) {
    return <Error404 />;
  }

  return (
    <>
      <Breadcrumbs aria-label='breadcrumb' sx={{ mb: 2 }}>
        <Typography color='text.primary'>Library</Typography>
        <Typography color='text.primary'>
          <Link to={ROUTE_STRING.DOCKER_IMAGE.LIST}>Docker Repositories</Link>
        </Typography>
        <Typography color='text.primary'>Deployment New Version</Typography>
      </Breadcrumbs>

      <Card sx={{ mb: '50px' }}>
        <AppsHeader>
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            sx={{ width: '100%' }}
          >
            <Box
              sx={{
                display: 'flex',
                cursor: 'pointer',
                alignItems: 'center',
                gap: 4,
              }}
              component='span'
              mr={{ xs: 2, sm: 4 }}
            >
              <AppTooltip title={<IntlMessages id='common.back' />}>
                <ArrowBackIcon
                  sx={{
                    color: 'text.secondary',
                  }}
                  onClick={onClickBackButton}
                />
              </AppTooltip>
              <Typography variant='h4' sx={{ cursor: 'auto' }}>
                Deployment New Version
              </Typography>
            </Box>
          </Box>
        </AppsHeader>

        <Box p={4}>
          {loading && <Code />}
          {!loading && (
            <Formik
              enableReinitialize
              validateOnChange={true}
              validateOnBlur={false}
              initialValues={{
                ...initialValues,
                DeploymentType: DockerAppTypes.find(
                  (e) =>
                    e.Value.toString() ===
                    initialValues.DeploymentType?.toString(),
                ),
                AppModel: data?.data?.App,
              }}
              onSubmit={handleSubmit}
              validationSchema={validationSchema.shape({
                AppModel: Yup.object().nullable().label('Marketplace App'),
              })}
            >
              {({
                submitForm,
                values,
                setFieldValue,
                errors,
                validateField,
              }) => (
                <AddDockerImageForm
                  isUpgrade
                  validateField={validateField}
                  setForm={setFieldValue}
                  submitForm={submitForm}
                  onCancel={onClickBackButton}
                  loading={addItem.isPending}
                  values={values}
                  errors={errors}
                />
              )}
            </Formik>
          )}
        </Box>
      </Card>
    </>
  );
};

export default React.memo(UpgradeVersion);
